import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, UserPlus } from 'lucide-react';

export default function Register() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organisationName: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { register } = useAuth();

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!formData.organisationName.trim()) {
      setError('Please tell us your organisation name');
      return;
    }

    setLoading(true);

    try {
      await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        organisation_name: formData.organisationName.trim(),
      });
      // Day 6 — tenant isolation is live across every operational route,
      // so register now sets the httpOnly auth cookie and we drop the
      // caller straight onto the dashboard. The `/trial-pending` splash
      // is retained for historical links but no longer part of the
      // happy path.
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#1ABC9C]">Elara</h1>
          <p className="text-[#C7C7C7] mt-2">Workforce Management System</p>
        </div>

        <Card className="bg-[#2B2B2B] border-white/5">
          <CardHeader>
            <CardTitle className="text-[#F2F2F2]">Start your trial</CardTitle>
            <CardDescription className="text-[#C7C7C7]">
              Create your organisation account. We'll set up your own private workspace — you'll never share data with another trial team.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
              {error && (
                <div
                  className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-md text-red-400 text-sm"
                  data-testid="register-error"
                >
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#F2F2F2]">Your Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Alex Morgan"
                  className="bg-[#1A1A1A] border-[#3D3D3D] text-[#F2F2F2] placeholder:text-[#C7C7C7]/50 focus:border-[#1ABC9C] focus:ring-[#1ABC9C]"
                  required
                  data-testid="register-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="organisationName" className="text-[#F2F2F2]">Organisation Name</Label>
                <Input
                  id="organisationName"
                  type="text"
                  value={formData.organisationName}
                  onChange={(e) => handleChange('organisationName', e.target.value)}
                  placeholder="Northside Medical Services"
                  className="bg-[#1A1A1A] border-[#3D3D3D] text-[#F2F2F2] placeholder:text-[#C7C7C7]/50 focus:border-[#1ABC9C] focus:ring-[#1ABC9C]"
                  required
                  data-testid="register-organisation-input"
                />
                <p className="text-xs text-[#A1A1AA]">
                  This becomes your private workspace. You'll be its first administrator.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#F2F2F2]">Work Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="you@yourorg.co.uk"
                  className="bg-[#1A1A1A] border-[#3D3D3D] text-[#F2F2F2] placeholder:text-[#C7C7C7]/50 focus:border-[#1ABC9C] focus:ring-[#1ABC9C]"
                  required
                  data-testid="register-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#F2F2F2]">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="Minimum 6 characters"
                  className="bg-[#1A1A1A] border-[#3D3D3D] text-[#F2F2F2] placeholder:text-[#C7C7C7]/50 focus:border-[#1ABC9C] focus:ring-[#1ABC9C]"
                  required
                  data-testid="register-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[#F2F2F2]">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  placeholder="Repeat your password"
                  className="bg-[#1A1A1A] border-[#3D3D3D] text-[#F2F2F2] placeholder:text-[#C7C7C7]/50 focus:border-[#1ABC9C] focus:ring-[#1ABC9C]"
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 text-white"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Request my trial
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-[#C7C7C7]">
              Already have an account?{' '}
              <Link to="/login" className="text-[#1ABC9C] hover:underline" data-testid="login-link">
                Sign In
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
